import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { fmtDate, eur, money, CURRENCY_SYMBOLS } from "../../lib/helpers";
import Card from "../../components/Card";
import HeadStat from "../../components/HeadStat";
import { MissionPanel } from "../../components/MissionPanel";
import RecordShell from "../../components/RecordShell";
import ContactButton from "../../components/ContactButton";
import CollabPayments from "../../components/CollabPayments";
import KpiSummary from "../../components/KpiSummary";
import { MissionEvaluations } from "../../components/Evaluation";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

export default function AdminMissionDetail() {
  const { confirm, ask } = useConfirm();
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);

  const load = useCallback(() => api.get(`/admin/missions/${id}`).then((r) => setM(r.data)).catch(() => nav("/admin/missions")), [id, nav]);
  useEffect(() => { load(); api.get("/admin/clients").then((r) => setClients(r.data)); api.get("/admin/projects").then((r) => setProjects(r.data)); api.get("/admin/modules").then((r) => setModules(r.data)); }, [id, load]);
  if (!m) return null;

  const closeMission = async () => {
    if (!(await confirm({ title: "Marquer la mission comme terminée ?", message: "Deux demandes d'évaluation (agence + collaborateur) seront envoyées.", confirmLabel: "Marquer terminée" }))) return;
    try {
      await api.post(`/admin/missions/${id}/close`);
      toast.success("Mission terminée — évaluations envoyées");
      window.dispatchEvent(new Event("eval-refresh"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const statusValue = m.status_override || (m.closed ? "terminee" : "auto");
  const changeStatus = async (val) => {
    if (val === statusValue) return;
    if (val === "terminee") { closeMission(); return; }
    if (val === "annulee" && !(await confirm({ title: "Marquer cette mission comme annulée ?", confirmLabel: "Marquer annulée" }))) return;
    try {
      await api.put(`/admin/missions/${id}/status`, { status: val });
      toast.success("Statut de la mission mis à jour");
      window.dispatchEvent(new Event("eval-refresh"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const renameMission = async () => {
    const name = await ask({ title: "Renommer la mission", label: "Nom de la mission", defaultValue: m.type, confirmLabel: "Renommer" });
    if (!name || !name.trim() || name.trim() === m.type) return;
    try { await api.put(`/admin/missions/${id}`, { type: name.trim() }); toast.success("Mission renommée"); load(); }
    catch { toast.error("Erreur lors du renommage"); }
  };

  const deleteMission = async () => {
    if (!(await confirm({ title: `Supprimer définitivement la mission « ${m.type} » ?`, message: "Cette action est irréversible.", confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/missions/${id}`); toast.success("Mission supprimée"); nav("/admin/missions"); }
    catch (e) { toast.error(e.response?.data?.detail || "Suppression impossible"); }
  };

  const fin = m.collab_financials || {};
  const cur = fin.currency || m.currency || "EUR";
  const np = fin.next_payment;

  const header = (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1">
            {m.module_label && <span className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux">{m.module_label}</span>}
          </div>
          <h1 className="font-display text-2xl text-marine">{m.type}</h1>
          <div className="text-sm text-marine/80 flex items-center gap-3 flex-wrap mt-0.5">
            <span>{m.collaborator_name}{m.collaborator_role ? ` · ${m.collaborator_role}` : ""}</span>
            <span>Rendu {fmtDate(m.deadline)}</span>
            <span>{m.progress ?? 0} %</span>
          </div>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:shrink-0">
          <button onClick={renameMission} data-testid="rename-mission-btn" title="Renommer la mission" className="w-8 h-8 rounded-sm border border-marine/20 flex items-center justify-center text-marine hover:bg-marine hover:text-white transition-colors"><Icons.Pencil size={16} /></button>
          {m.collaborator_id && (
            <ContactButton contextType="mission" contextId={m.id} contextLabel={m.type}
              recipients={[{ id: m.collaborator_id, name: m.collaborator_name || "Collaborateur", role: "collaborator" }]} testid="mission-contact" />
          )}
          <div className="flex items-center gap-1.5 border border-marine/20 rounded-sm pl-2.5 pr-1 py-1">
            <Icons.SlidersHorizontal size={16} className="text-marine" />
            <select aria-label="Statut de la mission" value={statusValue} onChange={(e) => changeStatus(e.target.value)} data-testid="mission-status-select"
              className="text-sm bg-transparent outline-none text-marine cursor-pointer pr-1" disabled={!m.collaborator_id}>
              <option value="auto">Automatique (selon phases)</option>
              <option value="en_pause">En pause</option>
              <option value="annulee">Annulée</option>
              <option value="terminee">Terminée</option>
            </select>
          </div>
          <button onClick={deleteMission} data-testid="mission-delete" title="Supprimer la mission" className="text-sm border border-rouge/30 text-rouge-texte rounded-sm px-3 py-1.5 hover:bg-rouge hover:text-white transition-colors flex items-center gap-1"><Icons.Trash2 size={16} /> Supprimer</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-y-3 [&>*:last-child:nth-child(odd)]:col-span-2 mt-4 pt-4 border-t border-marine/10 sm:flex sm:items-stretch sm:gap-1 sm:overflow-x-auto no-scrollbar sm:divide-x sm:divide-marine/10">
        <HeadStat label="Avancement" value={`${m.progress ?? 0}%`} />
        <HeadStat label="Budget" value={money(fin.total, cur)} />
        <HeadStat label="Payé" value={money(fin.paid, cur)} accent="text-bleu" />
        <HeadStat label="Reste" value={money(fin.reste_a_payer, cur)} accent="text-bordeaux" />
      </div>
    </div>
  );

  const renderApercu = () => (
    <div className="space-y-6">
      <KpiSummary testid="mission-kpis" items={[
        { testid: "kpi-progress", label: "Avancement", value: `${m.progress ?? 0} %` },
        { testid: "kpi-budget", label: "Budget mission", value: money(fin.total, cur) },
        { testid: "kpi-paid", accent: "text-bleu", label: "Déjà payé", value: money(fin.paid, cur) },
        { testid: "kpi-reste", accent: "text-bordeaux", label: "Reste à payer", value: money(fin.reste_a_payer, cur) },
        { testid: "kpi-next", label: "Prochaine échéance", value: np ? money(np.amount, cur) : "—", sub: np ? fmtDate(np.planned_date) : "Aucune échéance restante" },
      ]} />
      <Card><MissionPanel m={m} clients={clients} projects={projects} modules={modules} section="apercu" onReload={load} onNavigate={nav} /></Card>
    </div>
  );

  const renderPhases = () => <Card><MissionPanel m={m} clients={clients} projects={projects} modules={modules} section="phases" onReload={load} onNavigate={nav} /></Card>;
  const renderFichiers = () => <Card><MissionPanel m={m} clients={clients} projects={projects} modules={modules} section="fichiers" onReload={load} onNavigate={nav} /></Card>;
  const renderRemuneration = () => <CollabPayments mission={m} reload={load} />;
  // Conversation (01/10) : elle porte sa propre carte, pas de Card autour.
  const renderConversation = () => <MissionPanel m={m} clients={clients} projects={projects} modules={modules} section="conversation" onReload={load} onNavigate={nav} />;
  const renderEvaluations = () => <div><MissionEvaluations mid={id} /></div>;

  const sections = [
    { id: "apercu", label: "Vue d'ensemble", icon: "LayoutDashboard", render: renderApercu },
    { id: "phases", label: "Phases", icon: "ListChecks", render: renderPhases },
    { id: "fichiers", label: "Fichiers", icon: "FolderOpen", render: renderFichiers },
    { id: "remuneration", label: "Rémunération", icon: "Wallet", render: renderRemuneration },
    { id: "conversation", label: "Conversation", icon: "MessagesSquare", render: renderConversation },
    { id: "evaluations", label: "Évaluations", icon: "Star", render: renderEvaluations },
  ];

  return (
    <div>
      <button onClick={() => nav("/admin/missions")} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3"><Icons.ArrowLeft size={16} className="text-marine" /> Retour aux missions</button>
      <RecordShell
        testid="mission-record"
        breadcrumb={[
          { label: "Missions", to: "/admin/missions" },
          m.module_label ? { label: m.module_label, to: `/admin/modules/${m.module_key}/missions` } : null,
          { label: m.type },
        ].filter(Boolean)}
        header={header}
        sections={sections}
      />
    </div>
  );
}
